import { AbstractMesh, BoundingBox, Curve3, GroundMesh, InstancedMesh, Vector2, Vector3 } from '@babylonjs/core';
import { Builder, IBuilder } from 'builder-pattern';
import { Level } from './Level';
import { SpriteConfig } from './LevelConfig';

/**
 * Configuration for generating random positions inside of a level or bounds
 */
export class RandomPositionConfig {
  static builder(count: number): IBuilder<RandomPositionConfig> {
    return Builder(RandomPositionConfig, { count });
  }
  /**
   * The number of positions to generate
   */
  count: number;
  /**
   * The min coordinate of the bounds to generate positions in
   * If not set, zero will be used
   */
  min?: Vector3;
  /**
   * The max coordinate of the bounds to generate positions in
   * If not set, the level size will be used
   */
  max?: Vector3;
  /**
   * The bounding box to generate positions in
   * If not set, it will be calculated from the min and max coordinates
   */
  bounds?: BoundingBox;
  /**
   * The bounding boxes to exclude from the generated positions
   * If not set, no bounding boxes will be excluded
   */
  excludes?: BoundingBox[];
  /**
   * The y position of the generated positions
   * If not set, the y position will be random
   */
  y?: number | ((position: Vector3) => number);
  /**
   * The y offset of the generated positions
   * If not set, zero will be used
   */
  yOffset?: number;
  /**
   * If true, the first and last position will be the same
   */
  closed?: boolean;
  /**
   * If true, the generated positions will be grounded
   * This means that the y position will be set to the height of the ground at the x and z coordinates plus yOffset
   */
  grounded?: boolean;

  setDefaults(level: Level): void {
    if (!this.min) {
      this.min = new Vector3(0, 0, 0);
    }
    if (!this.max) {
      this.max = level.config.path.destination;
    }
    if (!this.bounds) {
      this.bounds = new BoundingBox(this.min, this.max);
    }
    if (!this.excludes) {
      this.excludes = [];
    }
    if (this.y === undefined) {
      this.y = (position: Vector3) => position.y;
    }
    if (this.yOffset === undefined) {
      this.yOffset = 0;
    }
  }
}

/**
 * Factory for generating positions inside of a level or polygon
 */
export class PositionFactory {
  static intersectionExcludes = ['skyBox_mesh'];

  constructor(public level: Level) {}

  /**
   * Generate a circle of positions
   *
   * @param center The center of the circle
   * @param radius The radius of the circle
   * @param count The number of positions to generate
   * @returns The generated positions
   */
  circle(center: Vector3, radius: number, count: number): Vector3[] {
    const positions: Vector3[] = [];
    const pi2 = Math.PI * 2;
    const step = pi2 / count;
    for (var i = 0; i < pi2; i += step) {
      var x = center.x + radius * Math.cos(i);
      var y = center.y + radius * Math.sin(i);
      var z = center.z + 0;
      positions.push(new Vector3(x, y, z));
    }
    return positions;
  }

  /**
   * Convert a 3D point array to a 2D point array
   *
   * @param points The 3D point array to convert
   * @returns The 2D point array
   */
  convertTo2D(points: Vector3[]): Vector2[] {
    const points2d = [];
    for (const point of points) {
      points2d.push(new Vector2(point.x, point.z));
    }
    return points2d;
  }

  /**
   * Convert a 3D point array to a 2D point array
   *
   * @param points The 3D point array to convert
   * @returns The 2D point array
   */
  convertTo2DPointArray(points: Vector3[]): Vector2[] {
    const points2d = [];
    for (const point of points) {
      points2d.push([point.x, point.z]);
    }
    return points2d;
  }

  /**
   * Generate a random position inside the bounds
   *
   * @returns A random position inside the bounds
   */
  randomPosition(bb?: BoundingBox): Vector3 {
    if (!bb) {
      bb = this.level.getBoundingBox();
    }
    const x = this.level.floatInRange(-bb.extendSize.x, bb.extendSize.x);
    const y = this.level.floatInRange(-bb.extendSize.y, bb.extendSize.y);
    const z = this.level.floatInRange(-bb.extendSize.x, bb.extendSize.x);
    return new Vector3(x, y, z);
  }

  /**
   * Check if a point is inside a polygon
   *
   * @param point The point to check
   * @param polygon The polygon to check
   * @returns True if the point is inside the polygon, false otherwise
   */
  isPointInPolygon(point: Vector2, polygon: Vector2[]): boolean {
    const x = point.x;
    const y = point.y;
    let isInside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;

      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

      if (intersect) {
        isInside = !isInside;
      }
    }

    return isInside;
  }

  /**
   * Generate random positions inside a polygon
   *
   * @param polygon The polygon to generate positions inside
   * @param count The number of positions to generate
   * @param y The y position of the generated positions
   * @returns The generated positions
   */
  randomPositionsInPolygon(polygon: Vector2[], count: number, y?: number | ((randomPoint: Vector2) => number)): Vector3[] {
    const positions: Vector3[] = [];
    for (let index = 0; index < count; index++) {
      const pos = this.randomPositionInPolygon(polygon, y);
      positions.push(pos);
    }
    return positions;
  }

  /**
   * Generate a random position inside a polygon
   *
   * @param polygon The polygon to generate a position inside
   * @param y The y position of the generated position
   * @returns The generated position
   */
  randomPositionInPolygon(polygon: Vector2[], y?: number | ((randomPoint: Vector2) => number)): Vector3 {
    // Find the bounding box of the polygon
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const point of polygon) {
      if (point.x < minX) minX = point.x;
      if (point.x > maxX) maxX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.y > maxY) maxY = point.y;
    }

    // Generate random points until we find one inside the polygon
    const maxAttempts = 10;
    let attempts = 0;

    while (attempts < maxAttempts) {
      const randomX = this.level.integerInRange(minX, maxX);
      const randomY = this.level.integerInRange(minY, maxY);
      const randomPoint = new Vector2(randomX, randomY);

      // Check if the random point is inside the polygon
      if (this.isPointInPolygon(randomPoint, polygon)) {
        let yPos = typeof y === 'function' ? y(randomPoint) : y;
        if (yPos === undefined) {
          yPos = 0;
        }
        return new Vector3(randomPoint.x, yPos, randomPoint.y);
      }

      attempts++;
    }

    // If no valid point was found, return undefined
    return undefined;
  }

  /**
   * Generate random positions by configuration
   *
   * @param config Configuration for generating random positions
   * @returns The generated positions
   */
  randomPositionsByConfig(config: RandomPositionConfig): Vector3[] {
    config.setDefaults(this.level);
    const positions: Vector3[] = [];
    for (let i = 0; i < config.count; i++) {
      const pos = this.randomPosition(config.bounds);

      if (config.y !== undefined) {
        if (typeof config.y === 'function') {
          pos.y = config.y(pos);
        } else {
          pos.y = config.y;
        }
      }
      if (config.excludes) {
        for (const exclude of config.excludes) {
          if (this.intersectsExisting(pos, exclude)) {
            continue;
          }
        }
      }
      if (config.grounded) {
        pos.y = this.level.getHeightAtCoordinates(pos.x, pos.z);
      }
      pos.y += config.yOffset;

      positions.push(pos);
    }

    if (config.closed) {
      positions.push(positions[0]);
    }
    return positions;
  }

  randomPositions(
    count: number,
    bounds: BoundingBox,
    grounded: boolean = false,
    exclude?: BoundingBox,
    filter?: (position: Vector3) => boolean
  ): Vector3[] {
    const positions: Vector3[] = [];
    const maxAttempts = count * 10;
    let attempts = 0;

    while (positions.length < count && attempts < maxAttempts) {
      const position = this.randomPosition(bounds);
      if (grounded) {
        position.y = this.level.getHeightAtCoordinates(position.x, position.z);
      }
      if (filter && !filter(position)) {
        continue;
      }
      // Check if position is inside the exclude bounding box
      if (!exclude || !exclude.intersectsPoint(position)) {
        positions.push(position);
      }
      attempts++;
    }

    return positions;
  }

  randomPositionsOnGround(count: number, yOffset: number = 0, closed: boolean = false, bounds?: BoundingBox): Vector3[] {
    const positions: Vector3[] = [];
    for (let i = 0; i < count; i++) {
      const p = this.randomPosition(bounds);
      p.y = this.level.getHeightAtCoordinates(p.x, p.z) + yOffset;
      positions.push(p);
    }
    if (closed) {
      positions.push(positions[0]);
    }
    return positions;
  }

  randomPositionsOnGroundMesh(ground: GroundMesh, count: number) {
    const positions: Vector3[] = [];
    const minX = ground.position.x - (ground.scaling.x * ground._width) / 2;
    const maxX = ground.position.x + (ground.scaling.x * ground._width) / 2;
    const minZ = ground.position.z - (ground.scaling.z * ground._height) / 2;
    const maxZ = ground.position.z + (ground.scaling.z * ground._height) / 2;

    for (let i = 0; i < count; i++) {
      const x = this.level.rng.uFloat32() * (maxX - minX) + minX;
      const z = this.level.rng.uFloat32() * (maxZ - minZ) + minZ;
      const y = ground.getHeightAtCoordinates(x, z);
      positions.push(new Vector3(x, y, z));
    }

    return positions;
  }

  randomNotColliding(count: number, closed: boolean = false): Vector3[] {
    const positions: Vector3[] = [];
    let tries = 1;
    while (positions.length < count && tries < count * 2) {
      const position = this.randomPosition();
      if (!this.pointIntersectsExisting(position)) {
        positions.push(position);
      }
      tries++;
    }
    if (closed) {
      positions.push(positions[0]);
    }
    return positions;
  }

  /**
   * Generate random positions inside of the level that do not intersect with existing meshes
   *
   * @param count The number of positions to generate
   * @param boundingBox The bounding box of the mesh to check for intersections
   * @returns The generated positions
   */
  randomNotIntersecting(count: number, boundingBox: BoundingBox): Vector3[] {
    const positions: Vector3[] = [];
    while (positions.length < count) {
      const position = this.randomPosition();
      if (!this.intersectsExisting(position, boundingBox)) {
        positions.push(position);
      }
    }
    return positions;
  }

  getIntersectionCandidates(): AbstractMesh[] {
    return this.level.game.scene.meshes.filter(
      (mesh) => mesh instanceof InstancedMesh && !PositionFactory.intersectionExcludes.includes(mesh.name)
    );
  }

  /**
   * Check if a position intersects with existing meshes
   *
   * @param position The position to check for intersections
   * @param boundingBox The bounding box of the mesh to check for intersections
   * @returns True if the position intersects with existing meshes, false otherwise
   */
  intersectsExisting(position: Vector3, boundingBox: BoundingBox): boolean {
    const min = boundingBox.minimum.add(position);
    const max = boundingBox.maximum.add(position);
    const meshes = this.getIntersectionCandidates();
    for (const sceneMesh of meshes) {
      if (sceneMesh.getBoundingInfo().boundingBox.intersectsMinMax(min, max)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if a point intersects with existing meshes
   *
   * @param point the point to check for intersections
   * @returns
   */
  pointIntersectsExisting(point: Vector3): boolean {
    const meshes = this.getIntersectionCandidates();
    for (const sceneMesh of meshes) {
      if (sceneMesh.intersectsPoint(point)) {
        return true;
      }
    }
    return false;
  }

  spritePositionsAtGround(sprite: SpriteConfig, bounds?: BoundingBox): Vector3[] {
    const y = (randomPoint: Vector2) => {
      const height = this.level.getHeightAtCoordinates(randomPoint.x, randomPoint.y);
      const scale = this.level.floatInRange(sprite.scale.min, sprite.scale.max);
      return height + scale / 2;
    };

    const count = sprite.capacity;
    const positions: Vector3[] = this.randomPositions(count, this.level.getBoundingBox(), true);
    for (const position of positions) {
      position.y = y(new Vector2(position.x, position.z));
    }
    return positions;
  }

  swirlPath(radius: number = 0.5, angle: number = 0, length: number = 1000, radiusStep: number = 0.3, angleStep: number = 0.1): Vector3[] {
    const points = [];
    for (var index = 0; index < length; index = index + 5) {
      points.push(new Vector3(radius * Math.cos(angle), 0, radius * Math.sin(angle)));
      radius += radiusStep;
      angle += angleStep;
    }
    return points;
  }

  circlePath(radius: number = 10, y: number = 0): Vector3[] {
    const points = [];
    for (var i = -Math.PI; i <= Math.PI; i += Math.PI / 360) {
      points.push(new Vector3(radius * Math.cos(i), y, radius * Math.sin(i)));
    }
    return points;
  }

  randomCatmullRomPath(points: number, steps: number = 10, closed: boolean = true): Vector3[] {
    const positions = this.randomNotColliding(points, closed);
    const catmullRom = Curve3.CreateCatmullRomSpline(positions, steps, closed);
    return catmullRom.getPoints();
  }

  randomQuadraticBezierPath(points: number): Vector3[] {
    const result: Vector3[] = [];
    const positions = this.randomPositions(points, this.level.getBoundingBox(), true);

    for (let index = 0; index < positions.length - 1; index++) {
      const position = positions[index];
      const quadraticBezierVectors = Curve3.CreateQuadraticBezier(
        position,
        position.add(
          new Vector3(this.level.integerInRange(-20, 20), this.level.integerInRange(-20, 20), this.level.integerInRange(-20, 20))
        ),
        position.add(positions[index + 1]),
        10
      );
      result.push(...quadraticBezierVectors.getPoints());
    }

    return result;
  }
}
